import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { asyncTaskResult, asyncTasksListing, sitesApi } from "@/network/network.jsx";
import { ASYNC_TASK_STATUS, ASYNC_TASK_TYPES } from "@/other/constants/OtherKeys.jsx";
import { shouldShowAsyncDialog } from "@/bryntum-lib/editors/AsyncTaskMessageEditor/SwivuAsyncMessagePopup";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys.jsx";
import { STORE_KEYS } from "@/other/constants/StoreKeys.jsx";
import { GRANTS } from "@/other/constants/PrivilegeTypes.jsx";
import { showToast } from "@/other/utils/utils.jsx";
import { getApiErrorMessage } from "@/integrations";
import { Site } from "@/bryntum-lib/models/Site";
import { MarketingPage } from "@/bryntum-lib/models/MarketingPage";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import {
  ensureSiteInStore,
  findSiteForBoard,
  getSitePublishButtonState,
  getSitePublishLiveButtonState,
  isSandboxBoardRecord,
  isSiteLive,
  mergeSitePublishFromApi,
  siteNeedsPersist,
  TEST_DRIVE_SITE_PAGE_UNAVAILABLE_MSG,
} from "../utils";

const POLL_INTERVAL_MS = 10_000;

type PublishSnapshot = {
  isPublishing: boolean;
  isRepublishing: boolean;
};

type DeploySnapshot = {
  isDeploying: boolean;
};

let publishSnapshot: PublishSnapshot = { isPublishing: false, isRepublishing: false };
let deploySnapshot: DeploySnapshot = { isDeploying: false };
const publishListeners = new Set<() => void>();
const deployListeners = new Set<() => void>();
let pollIntervalId: ReturnType<typeof setInterval> | null = null;
let pollSubscriberCount = 0;
let pollBoardId: number | undefined;
const publishTaskIdRef = { current: null as string | null };
const deployTaskIdRef = { current: null as string | null };
const processedPublishTaskIdsRef = { current: new Set<string>() };
const processedDeployTaskIdsRef = { current: new Set<string>() };
const manualPublishRef = { current: false };
const publishPollSessionRef = {
  current: { boardId: undefined as number | undefined, seeded: false },
};
const deployPollSessionRef = {
  current: { boardId: undefined as number | undefined, seeded: false },
};
const mergeSitePublishResultRef = {
  current: null as ((data: unknown) => void) | null,
};

function emitPublishChange() {
  publishListeners.forEach((listener) => listener());
}

function emitDeployChange() {
  deployListeners.forEach((listener) => listener());
}

function setPublishSnapshot(next: Partial<PublishSnapshot>) {
  publishSnapshot = { ...publishSnapshot, ...next };
  emitPublishChange();
}

function setDeploySnapshot(next: Partial<DeploySnapshot>) {
  deploySnapshot = { ...deploySnapshot, ...next };
  emitDeployChange();
}

function subscribePublish(listener: () => void) {
  publishListeners.add(listener);
  return () => publishListeners.delete(listener);
}

function subscribeDeploy(listener: () => void) {
  deployListeners.add(listener);
  return () => deployListeners.delete(listener);
}

function getPublishSnapshot() {
  return publishSnapshot;
}

function getDeploySnapshot() {
  return deploySnapshot;
}

function taskMatchesBoard(task: { meta_data?: { board_id?: number } }, boardId: number | undefined) {
  if (boardId == null) return false;
  return task.meta_data?.board_id === boardId;
}

type SiteAsyncTask = {
  id: number | string;
  type: number;
  status: string;
  meta_data?: { board_id?: number };
};

async function mergePublishTaskResult(taskId: string) {
  const response = await asyncTaskResult(taskId);
  mergeSitePublishResultRef.current?.(response?.data);
  return response;
}

async function showPublishTaskFailure(taskId: string, cancelled: boolean, fallback: string) {
  if (cancelled) {
    showToast({ msg: "Site publish cancelled.", type: "error", duration: 4000 });
    return;
  }

  try {
    const response = await asyncTaskResult(taskId);
    mergeSitePublishResultRef.current?.(response?.data);
    showToast({
      msg: getApiErrorMessage(response, fallback),
      type: "error",
      duration: 5000,
    });
  } catch (error) {
    showToast({
      msg: getApiErrorMessage(error, fallback),
      type: "error",
      duration: 5000,
    });
  }
}

function isPreviewPublishTask(task: SiteAsyncTask, boardId: number | undefined): boolean {
  if (!taskMatchesBoard(task, boardId)) return false;
  if (task.type !== ASYNC_TASK_TYPES.SITE_PUBLISH) return false;
  // publish-with-domain may enqueue SITE_PUBLISH — route that task through the deploy loop.
  const taskId = String(task.id);
  return deployTaskIdRef.current == null || deployTaskIdRef.current !== taskId;
}

function isDeployPublishTask(task: SiteAsyncTask, boardId: number | undefined): boolean {
  if (!taskMatchesBoard(task, boardId)) return false;
  if (task.type === ASYNC_TASK_TYPES.SITE_CUSTOM_DOMAIN_DEPLOY) return true;
  const taskId = String(task.id);
  return task.type === ASYNC_TASK_TYPES.SITE_PUBLISH && deployTaskIdRef.current === taskId;
}

async function pollSiteAsyncTasks(boardId: number | undefined) {
  if (!boardId) return;

  const publishSession = publishPollSessionRef.current;
  if (publishSession.boardId !== boardId) {
    publishSession.boardId = boardId;
    publishSession.seeded = false;
    processedPublishTaskIdsRef.current.clear();
  }
  const isInitialPublishSeed = !publishSession.seeded;

  const deploySession = deployPollSessionRef.current;
  if (deploySession.boardId !== boardId) {
    deploySession.boardId = boardId;
    deploySession.seeded = false;
    processedDeployTaskIdsRef.current.clear();
  }
  const isInitialDeploySeed = !deploySession.seeded;

  try {
    const res = await asyncTasksListing();
    const tasks: SiteAsyncTask[] = res.data ?? [];

    const publishTasks = tasks.filter((t) => isPreviewPublishTask(t, boardId));
    const deployTasks = tasks.filter((t) => isDeployPublishTask(t, boardId));

    let hasPendingPublish = false;

    for (const task of publishTasks) {
      const taskId = String(task.id);
      if (processedPublishTaskIdsRef.current.has(taskId)) continue;

      if (task.status === ASYNC_TASK_STATUS.PENDING) {
        hasPendingPublish = true;
        publishTaskIdRef.current = taskId;
        setPublishSnapshot({
          isPublishing: true,
          isRepublishing: !manualPublishRef.current,
        });
        continue;
      }

      const isTrackedTask = publishTaskIdRef.current === taskId;

      if (isInitialPublishSeed && !isTrackedTask) {
        processedPublishTaskIdsRef.current.add(taskId);
        continue;
      }

      processedPublishTaskIdsRef.current.add(taskId);

      if (publishTaskIdRef.current === taskId) {
        publishTaskIdRef.current = null;
        setPublishSnapshot({ isPublishing: false, isRepublishing: false });
        manualPublishRef.current = false;
      }

      if (task.status === ASYNC_TASK_STATUS.COMPLETED) {
        try {
          await mergePublishTaskResult(taskId);
          showToast({ msg: "Site published successfully.", type: "success", duration: 4000 });
        } catch (error) {
          showToast({
            msg: getApiErrorMessage(error, "Site publish finished but the result could not be loaded."),
            type: "error",
            duration: 5000,
          });
        }
      } else if (task.status === ASYNC_TASK_STATUS.FAILED || task.status === ASYNC_TASK_STATUS.CANCELLED) {
        await showPublishTaskFailure(
          taskId,
          task.status === ASYNC_TASK_STATUS.CANCELLED,
          "Site publish failed.",
        );
      }
    }

    if (!hasPendingPublish && publishTasks.every((t) => processedPublishTaskIdsRef.current.has(String(t.id)))) {
      setPublishSnapshot({ isPublishing: false, isRepublishing: false });
      manualPublishRef.current = false;
    }

    if (isInitialPublishSeed) {
      publishSession.seeded = true;
    }

    let hasPendingDeploy = false;

    for (const task of deployTasks) {
      const taskId = String(task.id);
      if (processedDeployTaskIdsRef.current.has(taskId)) continue;

      if (task.status === ASYNC_TASK_STATUS.PENDING) {
        hasPendingDeploy = true;
        deployTaskIdRef.current = taskId;
        setDeploySnapshot({ isDeploying: true });
        continue;
      }

      const isTrackedTask = deployTaskIdRef.current === taskId;

      if (isInitialDeploySeed && !isTrackedTask) {
        processedDeployTaskIdsRef.current.add(taskId);
        continue;
      }

      processedDeployTaskIdsRef.current.add(taskId);

      if (deployTaskIdRef.current === taskId) {
        deployTaskIdRef.current = null;
        setDeploySnapshot({ isDeploying: false });
      }

      if (task.status === ASYNC_TASK_STATUS.COMPLETED) {
        try {
          await mergePublishTaskResult(taskId);
          showToast({ msg: "Site published live successfully.", type: "success", duration: 4000 });
        } catch (error) {
          showToast({
            msg: getApiErrorMessage(error, "Publish live finished but the result could not be loaded."),
            type: "error",
            duration: 5000,
          });
        }
      } else if (task.status === ASYNC_TASK_STATUS.FAILED || task.status === ASYNC_TASK_STATUS.CANCELLED) {
        await showPublishTaskFailure(
          taskId,
          task.status === ASYNC_TASK_STATUS.CANCELLED,
          "Publish live failed.",
        );
      }
    }

    if (!hasPendingDeploy && deployTasks.every((t) => processedDeployTaskIdsRef.current.has(String(t.id)))) {
      setDeploySnapshot({ isDeploying: false });
    }

    if (isInitialDeploySeed) {
      deploySession.seeded = true;
    }
  } catch {
    // ignore transient poll errors
  }
}

function startSiteAsyncPoll(boardId: number | undefined) {
  pollBoardId = boardId;
  if (pollIntervalId != null) return;
  void pollSiteAsyncTasks(boardId);
  pollIntervalId = setInterval(() => void pollSiteAsyncTasks(boardId), POLL_INTERVAL_MS);
}

function stopSiteAsyncPoll() {
  if (pollIntervalId != null) {
    clearInterval(pollIntervalId);
    pollIntervalId = null;
  }
}

interface UseSiteOptions {
  initialSite?: Site | null;
  /** When true (Site page), persist phantom site on mount. */
  syncOnMount?: boolean;
}

export function useSite(options: UseSiteOptions = {}) {
  const { initialSite, syncOnMount = false } = options;
  const { project, selectedBoard, user, editorHandler } = useSwivuBryntumContext();
  const boardRecord = selectedBoard?.record as
    | { slug?: string; isSandbox?: boolean }
    | undefined;
  const boardSlug = boardRecord?.slug;
  const boardId = selectedBoard?.id as number | undefined;
  const isTestDrive = isSandboxBoardRecord(boardRecord);
  const isReadOnly = !user?.can?.(GRANTS.grant_four) || isTestDrive;

  const { version: pagesVersion } = useSwivuStore({ storeId: STORE_KEYS.marketingPageStore });
  const { version: sitesVersion } = useSwivuStore({ storeId: STORE_KEYS.sitesStore });

  const publishState = useSyncExternalStore(subscribePublish, getPublishSnapshot, getPublishSnapshot);
  const deployState = useSyncExternalStore(subscribeDeploy, getDeploySnapshot, getDeploySnapshot);

  const [activeSite, setActiveSite] = useState<Site | null>(initialSite ?? null);
  const [isEnsuring, setIsEnsuring] = useState(false);
  const [ensureError, setEnsureError] = useState<string | null>(null);

  const siteRecord = useMemo<Site | null>(() => {
    if (!project || !boardId) return null;
    const store = project.getCrudStore(STORE_KEYS.sitesStore);
    return findSiteForBoard(store, boardId);
  }, [project, sitesVersion, boardId]);

  const includedPages = useMemo(() => {
    if (!project) return [] as MarketingPage[];
    const store = project.getCrudStore(STORE_KEYS.marketingPageStore);
    const rows = (store?.query(() => true) as unknown as MarketingPage[]) ?? [];
    return rows.filter((p) => p.includeInSite);
  }, [project, pagesVersion]);

  const getOrCreateLocalSite = useCallback((): Site | null => {
    if (!project || !boardId) return null;
    const store = project.getCrudStore(STORE_KEYS.sitesStore);
    if (!store) return null;

    const existing =
      findSiteForBoard(store, boardId) ??
      (initialSite?.board === boardId ? initialSite : null);
    if (existing) return existing;

    const slug = boardSlug ?? "site";
    const added = store.add({
      board: boardId,
      slug,
      theme: { preset: "light" },
    }) as unknown as Site[];
    return added[0] ?? null;
  }, [project, boardId, boardSlug, initialSite]);

  const ensureSiteRecord = useCallback((): Site | null => {
    return getOrCreateLocalSite();
  }, [getOrCreateLocalSite]);

  const syncSiteIfNeeded = useCallback(async (): Promise<Site | null> => {
    if (!project || !boardId) {
      setEnsureError("Board is not ready yet. Try again in a moment.");
      return null;
    }

    const store = project.getCrudStore(STORE_KEYS.sitesStore);
    if (!store) {
      setEnsureError("Site store is unavailable.");
      return null;
    }

    const site = getOrCreateLocalSite();
    if (!site) {
      setEnsureError("Could not prepare site for this board.");
      return null;
    }

    if (!siteNeedsPersist(site)) {
      setActiveSite(site);
      setEnsureError(null);
      return site;
    }

    if (isReadOnly) {
      setActiveSite(site);
      setEnsureError("No site exists for this board yet.");
      return null;
    }

    setIsEnsuring(true);
    setEnsureError(null);
    try {
      ensureSiteInStore(store, site);
      await project.sync();

      const synced = findSiteForBoard(store, boardId);
      if (!synced || siteNeedsPersist(synced)) {
        throw new Error("Site sync did not persist");
      }

      setActiveSite(synced);
      return synced;
    } catch {
      setEnsureError("Could not create site for this board. Try again.");
      setActiveSite(site);
      return null;
    } finally {
      setIsEnsuring(false);
    }
  }, [project, boardId, getOrCreateLocalSite, isReadOnly]);

  useEffect(() => {
    if (!syncOnMount) return;
    void syncSiteIfNeeded();
  }, [syncOnMount, syncSiteIfNeeded]);

  useEffect(() => {
    if (!project || !boardId) return;
    const store = project.getCrudStore(STORE_KEYS.sitesStore);
    const synced = findSiteForBoard(store, boardId);
    if (!synced) return;
    if (!activeSite || siteNeedsPersist(activeSite) || synced.id === activeSite.id) {
      setActiveSite(synced);
    }
  }, [project, boardId, sitesVersion, activeSite]);

  useEffect(() => {
    if (!boardId) return undefined;
    pollSubscriberCount += 1;
    startSiteAsyncPoll(boardId);
    return () => {
      pollSubscriberCount -= 1;
      if (pollSubscriberCount === 0) {
        stopSiteAsyncPoll();
        pollBoardId = undefined;
      }
    };
  }, [boardId]);

  useEffect(() => {
    if (!project || !boardId) {
      mergeSitePublishResultRef.current = null;
      return undefined;
    }

    mergeSitePublishResultRef.current = (data: unknown) => {
      const store = project.getCrudStore(STORE_KEYS.sitesStore);
      const site = findSiteForBoard(store, boardId);
      if (site) mergeSitePublishFromApi(site, data);
    };

    return () => {
      mergeSitePublishResultRef.current = null;
    };
  }, [project, boardId]);

  const startPublish = useCallback(async () => {
    if (isTestDrive) {
      showToast({ msg: TEST_DRIVE_SITE_PAGE_UNAVAILABLE_MSG, type: "error", duration: 4000 });
      return false;
    }
    if (publishSnapshot.isPublishing) {
      return false;
    }
    if (!boardSlug) {
      showToast({ msg: "No board selected.", type: "error", duration: 3000 });
      return false;
    }
    manualPublishRef.current = true;
    setPublishSnapshot({ isRepublishing: false, isPublishing: true });
    try {
      const response = await sitesApi(boardSlug).publish();
      const ok =
        response?.status === 202 ||
        (response?.data && typeof response.data.id !== "undefined");
      if (!ok) {
        showToast({ msg: "Could not start site publish.", type: "error", duration: 3000 });
        setPublishSnapshot({ isPublishing: false, isRepublishing: false });
        manualPublishRef.current = false;
        return false;
      }
      publishTaskIdRef.current = String(response.data.id);
      if (shouldShowAsyncDialog("SITE_PUBLISH")) {
        (editorHandler as { openEditor: (...args: unknown[]) => void }).openEditor(
          EDITOR_KEYS.asyncTaskMessageEditor,
          null,
          { record: "SITE_PUBLISH" },
          { useExisting: true, by: "recordType" },
        );
      }
      if (pollBoardId !== boardId) {
        startSiteAsyncPoll(boardId);
      }
      return true;
    } catch {
      showToast({ msg: "Failed to start site publish.", type: "error", duration: 4000 });
      setPublishSnapshot({ isPublishing: false, isRepublishing: false });
      manualPublishRef.current = false;
      return false;
    }
  }, [boardSlug, boardId, editorHandler, isTestDrive]);

  const startPublishWithDomain = useCallback(async () => {
    if (isTestDrive) {
      showToast({ msg: TEST_DRIVE_SITE_PAGE_UNAVAILABLE_MSG, type: "error", duration: 4000 });
      return false;
    }
    if (deploySnapshot.isDeploying) {
      return false;
    }
    if (!boardSlug) {
      showToast({ msg: "No board selected.", type: "error", duration: 3000 });
      return false;
    }
    setDeploySnapshot({ isDeploying: true });
    try {
      const response = await sitesApi(boardSlug).publishWithDomain();
      const ok =
        response?.status === 202 ||
        (response?.data && typeof response.data.id !== "undefined");
      if (!ok) {
        showToast({ msg: "Could not start publish live.", type: "error", duration: 3000 });
        setDeploySnapshot({ isDeploying: false });
        return false;
      }
      deployTaskIdRef.current = String(response.data.id);
      if (pollBoardId !== boardId) {
        startSiteAsyncPoll(boardId);
      }
      return true;
    } catch (error) {
      showToast({
        msg: getApiErrorMessage(error, "Failed to start publish live."),
        type: "error",
        duration: 4000,
      });
      setDeploySnapshot({ isDeploying: false });
      return false;
    }
  }, [boardSlug, boardId, isTestDrive]);

  const siteReady = Boolean(
    activeSite && !siteNeedsPersist(activeSite) && !isEnsuring && !ensureError,
  );

  const publishSite = siteRecord ?? activeSite;
  const publishTaskPending =
    publishState.isPublishing || publishState.isRepublishing || deployState.isDeploying;

  const publishButtonState = useMemo(
    () =>
      getSitePublishButtonState({
        site: publishSite,
        publishTaskPending,
        siteReady,
        isReadOnly,
      }),
    [publishSite, publishTaskPending, siteReady, isReadOnly, sitesVersion],
  );

  const publishLiveButtonState = useMemo(
    () =>
      getSitePublishLiveButtonState({
        site: publishSite,
        publishTaskPending,
        siteReady,
        isReadOnly,
        isLive: isSiteLive(publishSite),
      }),
    [publishSite, publishTaskPending, siteReady, isReadOnly, sitesVersion],
  );

  return {
    activeSite,
    setActiveSite,
    siteRecord,
    ensureSiteRecord,
    isEnsuring,
    ensureError,
    syncSiteIfNeeded,
    isPublishing: publishState.isPublishing,
    isRepublishing: publishState.isRepublishing,
    isDeploying: deployState.isDeploying,
    publishTaskPending,
    startPublish,
    startPublishWithDomain,
    includedPages,
    publishEnabled: publishButtonState.enabled,
    publishTooltip: isTestDrive
      ? TEST_DRIVE_SITE_PAGE_UNAVAILABLE_MSG
      : publishButtonState.tooltip,
    publishLiveEnabled: publishLiveButtonState.enabled,
    publishLiveTooltip: isTestDrive
      ? TEST_DRIVE_SITE_PAGE_UNAVAILABLE_MSG
      : publishLiveButtonState.tooltip,
    hasUnpublishedChanges: publishButtonState.hasUnpublishedChanges,
    siteReady,
    isReadOnly,
    isTestDrive,
    testDriveTooltip: isTestDrive ? TEST_DRIVE_SITE_PAGE_UNAVAILABLE_MSG : null,
    boardSlug,
    boardId,
    project,
  };
}
